import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { ChevronDown20Regular, Search20Regular } from '@fluentui/react-icons';
import { useId, useMemo, useRef, useState } from 'react';
import type { ExplorerSourceSummary } from '../types';

interface SourcePickerComboboxProps {
  sources: ExplorerSourceSummary[];
  selectedSourceKey: string | null;
  onChange: (sourceKey: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

function matchesSource(source: ExplorerSourceSummary, query: string): boolean {
  const normalizedQuery = query.toLowerCase();

  return [
    source.displayName,
    source.description,
    source.databaseTypeName,
    source.executionModeName,
    source.sourceKey,
    ...source.tags.map((tag) => tag.label),
    ...source.groups.map((group) => group.label),
  ].some((candidate) => candidate.toLowerCase().includes(normalizedQuery));
}

export function SourcePickerCombobox({
  sources,
  selectedSourceKey,
  onChange,
  label = 'Source',
  placeholder = 'Search sources',
  disabled = false,
}: SourcePickerComboboxProps) {
  const fieldId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState('');
  const [showAllOptions, setShowAllOptions] = useState(false);
  const selectedSource = useMemo(
    () => sources.find((source) => source.sourceKey === selectedSourceKey) ?? null,
    [selectedSourceKey, sources]
  );
  const inputValue = query || selectedSource?.displayName || '';
  const filteredSources = useMemo(
    () => (showAllOptions || !query ? sources : sources.filter((source) => matchesSource(source, query))),
    [query, showAllOptions, sources]
  );

  function openAllOptions() {
    setShowAllOptions(true);
    inputRef.current?.focus();

    window.setTimeout(() => {
      inputRef.current?.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowDown', bubbles: true }));
    }, 0);
  }

  return (
    <div className="space-y-2">
      <label htmlFor={fieldId} className="block text-xs font-semibold text-slate-500">{label}</label>
      <Combobox
        value={selectedSource}
        by="sourceKey"
        immediate
        onChange={(source: ExplorerSourceSummary | null) => {
          if (!source) {
            return;
          }

          setQuery('');
          setShowAllOptions(false);
          onChange(source.sourceKey);
        }}
        onClose={() => setShowAllOptions(false)}
        disabled={disabled}
      >
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400">
            <Search20Regular />
          </div>
          <ComboboxInput
            id={fieldId}
            ref={inputRef}
            aria-label={label}
            className="w-full rounded-2xl border border-slate-300 bg-white py-2.5 pl-11 pr-12 text-sm text-slate-900 shadow-sm outline-none transition focus:border-sky-400"
            value={inputValue}
            onChange={(event) => {
              setShowAllOptions(false);
              setQuery(event.target.value);
            }}
            onFocus={() => setShowAllOptions(true)}
            onClick={() => setShowAllOptions(true)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                setShowAllOptions(true);
              }
            }}
            placeholder={placeholder}
          />

          <button
            type="button"
            aria-label={`Show all ${label.toLowerCase()} options`}
            aria-hidden={false}
            onMouseDown={(event) => event.preventDefault()}
            onClick={openAllOptions}
            className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-400 transition hover:text-slate-600"
          >
            <ChevronDown20Regular />
          </button>

          <ComboboxOptions className="absolute z-20 mt-2 max-h-80 w-full overflow-auto rounded-[1.5rem] border border-slate-200 bg-white p-2 shadow-2xl">
            {filteredSources.length === 0 ? (
              <div className="rounded-2xl px-4 py-3 text-sm text-slate-500">No sources match your search.</div>
            ) : (
              filteredSources.map((source) => (
                <ComboboxOption
                  key={source.sourceKey}
                  value={source}
                  className={({ focus }) => `cursor-default rounded-2xl px-4 py-3 ${focus ? 'bg-sky-50 text-slate-900' : 'text-slate-700'}`}
                >
                  <div className="text-sm font-semibold text-slate-900">{source.displayName}</div>
                  <div className="mt-1 text-sm text-slate-500">{source.description}</div>
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </div>
      </Combobox>
    </div>
  );
}

export default SourcePickerCombobox;